/**
 * Iter 244 · Shared inline-edit row components
 *
 * Extracted from the RFQ v2 list so the same "click to edit" pattern
 * (title, client/brand/owner popover picker, approval dots, comment/
 * activity popovers) can be reused on Estimates and Quotations lists.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, MessageSquare, History } from "lucide-react";
import api from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

export const APPROVAL_DOT = {
  approved: "bg-emerald-500",
  rejected: "bg-rose-500",
  revision_requested: "bg-amber-500",
  pending: "bg-slate-300",
};

export function ApprovalDots({ internal, client, labels = ["I", "C"] }) {
  const iCls = APPROVAL_DOT[internal || "pending"] || APPROVAL_DOT.pending;
  const cCls = APPROVAL_DOT[client || "pending"] || APPROVAL_DOT.pending;
  return (
    <div className="flex items-center gap-1.5 text-[10px]"
          title={`${labels[0]}: ${internal || "pending"} · ${labels[1]}: ${client || "pending"}`}>
      <div className="flex items-center gap-1">
        <span className={`inline-block w-2 h-2 rounded-full ${iCls}`} />
        <span className="text-muted-foreground">{labels[0]}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className={`inline-block w-2 h-2 rounded-full ${cCls}`} />
        <span className="text-muted-foreground">{labels[1]}</span>
      </div>
    </div>
  );
}

export function InlineText({ value, onSave, locked, testid, placeholder = "—" }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (locked || !editing) {
    return (
      <div className="flex items-center gap-1 group">
        <span className="truncate">{value || <span className="opacity-50">{placeholder}</span>}</span>
        {!locked && (
          <button type="button" onClick={() => setEditing(true)}
                   className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground shrink-0"
                   data-testid={`${testid}-edit`}>
            <Pencil className="w-2.5 h-2.5" />
          </button>
        )}
      </div>
    );
  }
  return (
    <input value={v} onChange={(e) => setV(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") { onSave(v); setEditing(false); }
              if (e.key === "Escape") { setV(value); setEditing(false); }
            }}
            onBlur={() => { if (v !== value) onSave(v); setEditing(false); }}
            className="w-full text-xs px-1 py-0.5 border border-primary bg-background focus:outline-none"
            data-testid={`${testid}-input`} />
  );
}

export function InlinePicker({ value, display, options, onSave, locked, testid }) {
  const [open, setOpen] = useState(false);
  if (locked) return <span className="truncate">{display}</span>;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
                 className="w-full text-left flex items-center gap-1 hover:bg-muted/50 px-1 py-0.5 truncate group"
                 data-testid={`${testid}-btn`}>
          <span className="truncate">{display}</span>
          <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-2 rounded-none w-64 z-[1300]">
        <SearchableSelect
          value={value}
          onValueChange={(v) => { onSave(v); setOpen(false); }}
          options={options}
          placeholder="Search…"
        />
      </PopoverContent>
    </Popover>
  );
}

export function CommentsPopover({ docType, docId, count = 0, endpoint }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(endpoint || `/${docType}s/${docId}/comments`)
      .then(r => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, [open, docType, docId, endpoint]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
                 className="relative text-[10px] px-1 py-1 text-muted-foreground hover:text-foreground"
                 title="Comments"
                 data-testid={`${docType}-comments-btn-${docId}`}>
          <MessageSquare className="w-3 h-3" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 text-[8px] bg-primary text-primary-foreground rounded-full w-3.5 h-3.5 flex items-center justify-center font-semibold">
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-3 w-80 z-[1300]">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Comments</div>
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-muted-foreground">No comments yet.</div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {items.map((c) => (
              <div key={c.id} className="text-xs border-l-2 border-border pl-2">
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <b className="text-foreground">{c.author_name || c.author_id || "—"}</b>
                  <span>· {fmtRelative(c.created_at)}</span>
                </div>
                <div className="mt-0.5 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 pt-2 border-t border-border text-right">
          <Link to={`/app/${docType}s/${docId}`} className="text-[11px] text-primary hover:underline">Open full thread →</Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ActivityPopover({ docType, docId, extras = [] }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get(`/${docType}s/${docId}/audit`).then(r => r.data || []).catch(() => []),
      ...extras.map((ep) => api.get(ep).then(r => r.data?.history || r.data || []).catch(() => [])),
    ]).then((results) => {
      const merged = [
        ...(results[0] || []).map(a => ({ kind: "audit", ...a })),
        ...results.slice(1).flat().map(a => ({ kind: "approval", ...a, created_at: a.at || a.created_at })),
      ].sort((x, y) => (y.created_at || y.at || "").localeCompare(x.created_at || x.at || ""));
      setRows(merged.slice(0, 15));
    }).finally(() => setLoading(false));
  }, [open, docType, docId, extras]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button"
                 className="text-[10px] px-1 py-1 text-muted-foreground hover:text-foreground"
                 title="Activity"
                 data-testid={`${docType}-activity-btn-${docId}`}>
          <History className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-3 w-96 z-[1300]">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recent activity</div>
        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground">No activity yet.</div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto text-xs">
            {rows.map((r, i) => (
              <div key={i} className="border-l-2 border-border pl-2">
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                  <span className={`uppercase tracking-widest ${r.kind === "approval" ? "text-primary" : ""}`}>
                    {r.kind === "approval" ? r.decision : (r.action || "edit")}
                  </span>
                  <span>· {fmtRelative(r.created_at || r.at)}</span>
                </div>
                <div className="mt-0.5">
                  <b>{r.actor_name || r.recorded_by_name || "System"}</b>
                  {r.kind === "approval" && r.revision_no && (
                    <span className="text-muted-foreground"> · rev {r.revision_no}</span>
                  )}
                  {r.note && <div className="text-muted-foreground italic mt-0.5 truncate">&ldquo;{r.note}&rdquo;</div>}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 pt-2 border-t border-border text-right">
          <Link to={`/app/${docType}s/${docId}`} className="text-[11px] text-primary hover:underline">Open audit trail →</Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
